import React, { FC, useState, useCallback, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Tab, Tabs } from '@cld/tabs';
import { globalTheme } from '@cld/component-theme';
import { messages } from '../../../i18n';
import { CodeTabSectionProps } from '../../../types';
import { CodeTab } from './CodeTab';
import { NO_SECTION } from './constants';

export const SIDEBAR_WIDTH = '380';

const Root = styled.div<{ isExpanded: boolean }>`
  min-height: calc(36 * ${globalTheme.spacing.lg});
  max-height: 100%;
  width: ${SIDEBAR_WIDTH}px;
  align-self: stretch;
  background-color: ${globalTheme.palette.surface};
  position: absolute;
  z-index: 1;
  box-shadow: ${globalTheme.shadow.lg};
  display: flex;
  flex-direction: column;

  ${(props) =>
    props.isExpanded &&
    css`
      width: calc(${globalTheme.spacing.lg} * 46);
    `}
`;

const StyledTabs = styled(Tabs)`
  flex: none;

  /* Keep the tabs equal width */
  & > * {
    flex: 1;
  }
`;

const Views = styled.div`
  flex: 1;
  overflow: auto;
`;

enum SidebarTabId {
  Setting = 'settings',
  Code = 'code',
}

export interface SidebarProps {
  settingsTab: ReactNode;
  codeTabSections: FC<CodeTabSectionProps>[];
}

export const Sidebar: FC<SidebarProps> = ({ settingsTab, codeTabSections }) => {
  const [activeTab, setActiveTab] = useState(SidebarTabId.Setting);
  const [expandedSectionIndex, setExpandedSectionIndex] = useState(NO_SECTION);
  const someSectionIsExpanded = expandedSectionIndex !== NO_SECTION;

  const handleSettingsTabClick = useCallback(() => {
    setActiveTab(SidebarTabId.Setting);
    setExpandedSectionIndex(NO_SECTION);
  }, []);

  const handleCodeTabClick = useCallback(() => {
    setActiveTab(SidebarTabId.Code);
  }, []);

  const handleExpandToggle = useCallback((sectionIndex: number) => {
    setExpandedSectionIndex((prevExpandedSectionIndex) => prevExpandedSectionIndex === sectionIndex ? NO_SECTION : sectionIndex);
  }, []);

  return (
    <Root isExpanded={someSectionIsExpanded}>
      <StyledTabs activeTabId={activeTab}>
        <Tab id={SidebarTabId.Setting} aria-controls="settings-tab" onClick={handleSettingsTabClick} data-test="settings-tab">
          <FormattedMessage {...messages['tabSettings.label']} />
        </Tab>
        <Tab id={SidebarTabId.Code} aria-controls="code-tab" onClick={handleCodeTabClick} data-test="code-tab">
          <FormattedMessage {...messages['tabCode.label']} />
        </Tab>
      </StyledTabs>
      <Views>
        {activeTab === SidebarTabId.Setting && (
          <div id="settings-tab" aria-labelledby={SidebarTabId.Setting}>
            {settingsTab}
          </div>
        )}
        {activeTab === SidebarTabId.Code && (
          <div id="code-tab" aria-labelledby={SidebarTabId.Code}>
            <CodeTab sections={codeTabSections} expandedSectionIndex={expandedSectionIndex} onExpandToggle={handleExpandToggle} />
          </div>
        )}
      </Views>
    </Root>
  );
};
